/**
 * O rodapé com a versão do front e da API — o que se copia num chamado para
 * dizer exatamente qual código estava rodando.
 *
 * As duas versões aparecem porque os dois sobem em deploys separados: o front
 * pode estar numa e a API em outra.
 *
 * **Passar o mouse mostra o que mudou.** Cada lado abre um cartão com o commit,
 * a data do build e as notas do NOVIDADES.md da versão no ar e das anteriores —
 * as do front vêm no próprio bundle, as da API vêm do `/health` dela.
 */

import { useEffect, useId, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { ListaDeNovidades } from "./ListaDeNovidades"
import { novidadesAte, type NovidadesDaVersao } from "./novidades"
import { esteBuild, notasValidas, novidadesDesteBuild, rotuloDaVersao } from "./versao"

interface SaudeDaApi {
  versao?: string | null
  commit?: string | null
  buildDate?: string | null
  novidades?: unknown
}

const ALTURA_DO_RODAPE = 20

function dataDoBuild(iso: string): string {
  const data = new Date(iso)
  return Number.isNaN(data.getTime())
    ? iso
    : data.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" })
}

export function RodapeDeVersao() {
  // Uma vez por sessão: o /health não toca o banco, mas não há por que repetir.
  // Quando a API mudar, quem recarrega a página (pelo aviso) lê de novo.
  const { data: api, isError } = useQuery({
    queryKey: ["versao-da-api"],
    queryFn: () => obter<SaudeDaApi>("health"),
    staleTime: Infinity,
    gcTime: Infinity,
  })

  const front = rotuloDaVersao(esteBuild.versao, esteBuild.commit)
  const textoApi = api?.versao ? rotuloDaVersao(api.versao, api.commit) : isError ? "indisponível" : "…"

  return (
    <footer
      style={{
        height: ALTURA_DO_RODAPE, flex: `0 0 ${ALTURA_DO_RODAPE}px`,
        display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12,
        padding: "0 14px", background: "var(--bg)", borderTop: "1px solid var(--line)",
        fontSize: 10.5, fontFamily: "var(--fm)", color: "var(--txt3)",
        whiteSpace: "nowrap", userSelect: "text",
      }}
    >
      <ParteDaVersao
        nome="Sistema"
        rotulo={front}
        versao={esteBuild.versao}
        detalhes={[
          esteBuild.commit ? `commit ${esteBuild.commit}` : null,
          `build ${dataDoBuild(esteBuild.build)}`,
        ]}
        notas={novidadesDesteBuild}
      />
      <span aria-hidden="true">|</span>
      <ParteDaVersao
        nome="API"
        rotulo={textoApi}
        versao={api?.versao ?? null}
        detalhes={[
          api?.commit ? `commit ${api.commit}` : null,
          api?.buildDate ? `build ${api.buildDate}` : null,
        ]}
        notas={notasValidas(api?.novidades)}
      />
    </footer>
  )
}

/**
 * "Sistema v2.4.0 · a1b2c3d", e o cartão que abre em cima dele.
 *
 * Abre no hover e no foco do teclado, e continua aberto enquanto o mouse está no
 * cartão — dá para rolar e selecionar o texto. O texto do rodapé continua
 * selecionável para colar num chamado.
 */
function ParteDaVersao({ nome, rotulo, versao, detalhes, notas }: {
  nome: string
  rotulo: string
  versao: string | null
  detalhes: (string | null)[]
  notas: readonly NovidadesDaVersao[]
}) {
  const [aberto, setAberto] = useState(false)
  const fechando = useRef<ReturnType<typeof setTimeout>>()
  const id = useId()

  // Um respiro para o mouse atravessar do rótulo até o cartão sem ele sumir no caminho.
  const abrir = () => { clearTimeout(fechando.current); setAberto(true) }
  const fechar = () => { clearTimeout(fechando.current); fechando.current = setTimeout(() => setAberto(false), 150) }
  useEffect(() => () => clearTimeout(fechando.current), [])

  const daVersao = versao ? novidadesAte(notas, versao, 3) : []

  return (
    <span
      style={{ position: "relative" }}
      onMouseEnter={abrir}
      onMouseLeave={fechar}
      onFocus={abrir}
      onBlur={fechar}
      onKeyDown={(e) => { if (e.key === "Escape") setAberto(false) }}
    >
      <span tabIndex={0} aria-describedby={aberto ? id : undefined} style={{ cursor: "help", outlineOffset: 2 }}>
        {nome} {rotulo}
      </span>

      {aberto && (
        <div
          id={id}
          role="tooltip"
          style={{
            position: "absolute", right: 0, bottom: "calc(100% + 6px)", zIndex: 70,
            width: 360, maxWidth: "calc(100vw - 24px)", maxHeight: "min(420px, 70vh)", overflowY: "auto",
            padding: "10px 12px", borderRadius: 10, whiteSpace: "normal", userSelect: "text",
            background: "var(--surface)", color: "var(--txt)", border: "1px solid var(--line2)",
            boxShadow: "0 10px 30px rgba(24, 16, 32, .18)", cursor: "auto",
          }}
        >
          <div style={{ fontFamily: "var(--ff)", fontSize: 12.5, fontWeight: 650 }}>
            {nome} {versao ? `v${versao}` : rotulo}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--txt3)", margin: "2px 0 8px", wordBreak: "break-all" }}>
            {detalhes.filter(Boolean).join(" · ")}
          </div>
          {daVersao.length > 0 ? (
            <ListaDeNovidades versoes={daVersao} limite={14} />
          ) : (
            <div style={{ fontFamily: "var(--ff)", fontSize: 11.5, color: "var(--txt3)" }}>
              {versao ? "Sem notas de mudança para esta versão." : "A versão ainda não chegou."}
            </div>
          )}
        </div>
      )}
    </span>
  )
}
